// Real Projects Data from CV
export const realProjects = [
  {
    id: 1,
    title: "Recruitment Web Application",
    description: "Advanced recruitment platform with secure authentication, CV upload/analysis with NLP, performance scoring, and quiz system for skill assessment.",
    fullDescription: `A comprehensive recruitment web application built for Techdoz that streamlines the hiring process through automation and AI-powered analysis. The platform features secure user authentication, automated CV screening using Natural Language Processing, candidate performance scoring, and an integrated quiz system for technical skill assessment.

The application provides a responsive admin interface that displays insights based on CV analysis and quiz results, making it easier for recruiters to identify top candidates efficiently.`,
    image: "/assets/Projects/Recruitment Web Application.png",
    gallery: [
      "/assets/Projects/Recruitment Web Application.png",
      "/assets/Projects/Recruitment Web Application.png",
      "/assets/Projects/Recruitment Web Application.png"
    ],
    techStack: ["Flask", "React.js", "NLTK", "MySQL", "AWS"],
    type: "Web App",
    industry: "HR Tech",
    status: "Live",
    year: "2025",
    featured: true,
    liveUrl: "https://recruitment-app-demo.com",
    githubUrl: "https://github.com/lahiru/recruitment-app",
    metrics: [
      { label: "CV Analysis", value: "NLP-Powered" },
      { label: "Platform", value: "Full-Stack" },
      { label: "Deployment", value: "AWS" },
      { label: "Status", value: "Production" }
    ],
    features: [
      "Secure user authentication with CV upload",
      "CV analysis using Natural Language Processing (NLTK)",
      "Performance scoring and candidate ranking",
      "Quiz system for skill assessment",
      "Responsive admin interface",
      "Real-time candidate insights"
    ],
    highlights: [
      "Automated CV screening reduces manual review time by 70%",
      "NLP-powered analysis identifies key skills and experience",
      "Integrated skill assessment through customizable quizzes",
      "AWS deployment ensures scalability and reliability"
    ],
    architecture: `Built with Flask backend for robust API development, React.js frontend for responsive user interface, NLTK for natural language processing of CVs, MySQL for reliable data storage, and AWS for cloud deployment and scalability.`
  },
  {
    id: 2,
    title: "X-ray Identification of Pneumonia and Tuberculosis System",
    description: "Web-based AI solution to detect pneumonia and tuberculosis from X-ray images, enhancing healthcare diagnostics with machine learning.",
    fullDescription: `An innovative healthcare diagnostic tool that leverages machine learning to analyze X-ray images for early detection of pneumonia and tuberculosis. The system provides accurate image processing, fast diagnostics, and an intuitive interface for healthcare professionals.

This project demonstrates the practical application of AI in addressing global health challenges, supporting early and accurate disease detection to aid in timely treatment.`,
    image: "/assets/Projects/X-ray Identification of Pneumonia and Tuberculosis System.png",
    gallery: [
      "/assets/Projects/X-ray Identification of Pneumonia and Tuberculosis System.png",
      "/assets/Projects/X-ray Identification of Pneumonia and Tuberculosis System.png",
      "/assets/Projects/X-ray Identification of Pneumonia and Tuberculosis System.png"
    ],
    techStack: ["Python", "Flask", "Django", "React", "TensorFlow", "Keras"],
    type: "Web App",
    industry: "Healthcare",
    status: "Completed",
    year: "2024",
    featured: true,
    githubUrl: "https://github.com/lahiru/xray-detection",
    metrics: [
      { label: "Accuracy", value: "High" },
      { label: "ML Framework", value: "TensorFlow" },
      { label: "Use Case", value: "Medical" },
      { label: "Impact", value: "Healthcare" }
    ],
    features: [
      "Accurate image processing and analysis",
      "Fast pneumonia and tuberculosis detection",
      "Intuitive interface for healthcare professionals",
      "Machine learning model training and optimization"
    ],
    highlights: [
      "Supports early disease detection for better treatment outcomes",
      "Utilizes CNN models for accurate X-ray analysis",
      "Addresses global health challenges with AI",
      "User-friendly interface for medical professionals"
    ],
    architecture: `Frontend built with React for healthcare professional interface, backend powered by Flask/Django with Python, machine learning models built using TensorFlow and Keras for image classification and disease detection.`
  },
  {
    id: 3,
    title: "SmartRecipe-AI Mobile App",
    description: "AI-powered ingredient recognition system with real-time image processing, personalized recipe generation, and comprehensive user preference management.",
    fullDescription: `SmartRecipe-AI is a cutting-edge mobile application that transforms the cooking experience through artificial intelligence. The app combines computer vision, cloud storage, real-time database management, and API integration to create a seamless user experience from image capture to recipe delivery.

The application features an AI-powered ingredient recognition system using Google Cloud Vision API and Gemini API, personalized recipe generation based on cuisine preferences and nutritional analysis, integrated grocery management with favorites and history tracking, and comprehensive preference management including dietary restrictions and favorite recipes.`,
    image: "/assets/Projects/SmartRecipe-AI Mobile App.png",
    gallery: [
      "/assets/Projects/SmartRecipe-AI Mobile App.png",
      "/assets/Projects/SmartRecipe-AI Mobile App.png",
      "/assets/Projects/SmartRecipe-AI Mobile App.png"
    ],
    techStack: ["Flutter", "Dart", "Firebase", "Google Cloud Vision API", "Gemini API", "AI/ML"],
    type: "Mobile App",
    industry: "Food Tech",
    status: "Live",
    year: "2024",
    featured: false,
    githubUrl: "https://github.com/lahiru/smartrecipe-ai",
    metrics: [
      { label: "Platform", value: "Cross-platform" },
      { label: "AI Integration", value: "Google Cloud" },
      { label: "Database", value: "Firebase" },
      { label: "Recognition", value: "Real-time" }
    ],
    features: [
      "AI-powered ingredient recognition",
      "Real-time image processing",
      "Personalized recipe generation",
      "Cuisine preference integration",
      "Nutritional analysis and management",
      "Favorites/history tracking"
    ],
    highlights: [
      "Reduces food waste by transforming ingredients into recipes",
      "Demonstrates practical AI application in consumer scenarios",
      "End-to-end mobile development with computer vision and cloud storage",
      "Real-time database integration for seamless user experience"
    ],
    architecture: `Cross-platform mobile development using Flutter/Dart with Firebase backend integration for Authentication, Firestore, and Storage. AI/ML implementation via Google Cloud Vision API and Gemini API for intelligent ingredient recognition and recipe recommendations.`
  },
  {
    id: 4,
    title: "AutoFix-LK Mobile App",
    description: "Multi-platform app with real-time booking, location services, e-commerce integration, and business analytics dashboard.",
    fullDescription: `AutoFix-LK is a comprehensive digital solution for Sri Lanka's automotive industry, addressing specific market needs and regional gaps with a localized approach. The platform serves multiple user types with role-based access, inventory management, and integrated communication systems.

The application features a complex B2B2C platform architecture, multi-stakeholder design serving customers and salon owners with revenue-enhancing features, operational efficiency tools, and data-driven insights.`,
    image: "/assets/Projects/AutoFix-LK Mobile App.png",
    gallery: [
      "/assets/Projects/AutoFix-LK Mobile App.png",
      "/assets/Projects/AutoFix-LK Mobile App.png",
      "/assets/Projects/AutoFix-LK Mobile App.png"
    ],
    techStack: ["Flutter", "Firebase", "Google Maps API", "Cloud Synchronization"],
    type: "Mobile App",
    industry: "Automotive",
    status: "Live",
    year: "2024",
    featured: false,
    githubUrl: "https://github.com/lahiru/autofix-lk",
    metrics: [
      { label: "User Types", value: "Multi-role" },
      { label: "Features", value: "B2B2C" },
      { label: "Market", value: "Sri Lanka" },
      { label: "Integration", value: "Real-time" }
    ],
    features: [
      "Real-time booking and location services",
      "E-commerce integration",
      "Emergency assistance",
      "Business analytics dashboard",
      "Multi-stakeholder architecture",
      "Inventory management"
    ],
    highlights: [
      "Addresses specific Sri Lankan automotive market needs",
      "Multi-user type platform with role-based access",
      "Real-time communication and booking system",
      "Comprehensive business analytics for salon owners"
    ],
    architecture: `Flutter cross-platform development with Firebase backend, Google Maps API integration for location services, real-time cloud synchronization, and comprehensive dashboard for business insights.`
  },
  {
    id: 5,
    title: "Ceylon Cinema Web Project",
    description: "Production-ready cinema management platform with interactive seat selection, secure PayPal payment processing, and comprehensive admin dashboard.",
    fullDescription: `Ceylon Cinema is a complete cinema management system built from scratch with clean MVC architecture, real-time data management, role-based access control, and scalable backend infrastructure suitable for commercial deployment.

The platform provides an enterprise-level web application with interactive seat selection, secure payment integration, automated digital ticket generation, and a comprehensive admin dashboard with analytics and content management.`,
    image: "/assets/Projects/Ceylon Cinema Web Project.png",
    gallery: [
      "/assets/Projects/Ceylon Cinema Web Project.png",
      "/assets/Projects/Ceylon Cinema Web Project.png",
      "/assets/Projects/Ceylon Cinema Web Project.png"
    ],
    techStack: ["Java EE", "Servlets", "JSP", "MySQL", "HTML5", "CSS3", "JavaScript", "Bootstrap", "PayPal API", "Apache Tomcat"],
    type: "Web App",
    industry: "Entertainment",
    status: "Completed",
    year: "2024",
    featured: false,
    githubUrl: "https://github.com/lahiru/ceylon-cinema",
    metrics: [
      { label: "Architecture", value: "MVC" },
      { label: "Payment", value: "PayPal" },
      { label: "Server", value: "Tomcat" },
      { label: "Database", value: "MySQL" }
    ],
    features: [
      "Complete cinema management system",
      "Interactive real-time seat selection",
      "Secure PayPal payment integration",
      "Automated ticketing and delivery",
      "Comprehensive admin dashboard",
      "Role-based access control"
    ],
    highlights: [
      "Production-ready platform built from scratch",
      "Clean MVC architecture with scalable backend",
      "Secure payment processing with financial workflows",
      "Enterprise-level e-commerce and content management"
    ],
    architecture: `Full-stack Java EE development with Servlets and JSP, MySQL database integration, frontend with HTML5, CSS3, JavaScript, and Bootstrap, PayPal API for secure payments, deployed on Apache Tomcat servers.`
  },
  {
    id: 6,
    title: "BarberGo Mobile Project",
    description: "Complete salon management solution with instant appointment booking, comprehensive admin dashboard, and integrated promotional deals system.",
    fullDescription: `BarberGo is a mobile-first application addressing pain points in traditional barbering industry, creating streamlined booking workflows that eliminate queue management issues and enhance customer satisfaction through technology.

The platform optimizes business processes for both customers and salon owners, providing operational efficiency tools, data-driven insights, and revenue-enhancing features simultaneously.`,
    image: "/assets/Projects/BarberGo Mobile Project.png",
    gallery: [
      "/assets/Projects/BarberGo Mobile Project.png",
      "/assets/Projects/BarberGo Mobile Project.png",
      "/assets/Projects/BarberGo Mobile Project.png"
    ],
    techStack: ["Flutter", "Firebase", "Cloud Firestore", "Material Design", "Real-time Sync"],
    type: "Mobile App",
    industry: "Service",
    status: "Live",
    year: "2024",
    featured: false,
    githubUrl: "https://github.com/lahiru/barbergo",
    metrics: [
      { label: "Platform", value: "Mobile-first" },
      { label: "Users", value: "Dual-sided" },
      { label: "Sync", value: "Real-time" },
      { label: "Design", value: "Material" }
    ],
    features: [
      "Instant appointment booking system",
      "Smart cart functionality for service bundling",
      "Comprehensive admin dashboard",
      "Real-time updates and sync",
      "Integrated promotional deals",
      "Service industry optimization"
    ],
    highlights: [
      "Addresses traditional barbering industry pain points",
      "Streamlined workflows eliminate queue issues",
      "Dual-sided platform serving customers and owners",
      "Demonstrates business outcome improvement through technology"
    ],
    architecture: `Cross-platform Flutter development with Firebase Cloud Firestore, real-time data synchronization, Material Design implementation, and comprehensive admin tools for business management.`
  },
  {
    id: 7,
    title: "ToyMingle Mobile Project",
    description: "Toy marketplace with swap functionality, category search, real-time messaging, secure payments, and user preference management.",
    fullDescription: `ToyMingle is a circular economy platform promoting toy reuse and sustainability while providing cost-effective family solutions. The application features a social commerce marketplace with safety measures, real-time communication, and trust-building for peer-to-peer family transactions.

The platform combines e-commerce with environmental consciousness, demonstrating how technology can support sustainable consumer behaviors.`,
    image: "/assets/Projects/ToyMingle Mobile Project.png",
    gallery: [
      "/assets/Projects/ToyMingle Mobile Project.png",
      "/assets/Projects/ToyMingle Mobile Project.png",
      "/assets/Projects/ToyMingle Mobile Project.png"
    ],
    techStack: ["Flutter", "Firebase", "PayPal API", "Real-time Chat"],
    type: "Mobile App",
    industry: "E-commerce",
    status: "Completed",
    year: "2024",
    featured: false,
    githubUrl: "https://github.com/lahiru/toymingle",
    metrics: [
      { label: "Model", value: "C2C Platform" },
      { label: "Focus", value: "Sustainability" },
      { label: "Features", value: "Swap+Buy" },
      { label: "Security", value: "Verified" }
    ],
    features: [
      "Toy marketplace with swap functionality",
      "Category search and filtering",
      "Real-time messaging system",
      "Secure payments with flash deals",
      "User preference management",
      "Trust and safety measures"
    ],
    highlights: [
      "Circular economy platform for toy sustainability",
      "Cost-effective solution for families",
      "Social commerce with peer-to-peer transactions",
      "Combines e-commerce with environmental consciousness"
    ],
    architecture: `Flutter mobile development with Firebase backend, PayPal API integration for secure payments, real-time chat implementation, and comprehensive user verification system.`
  },
  {
    id: 8,
    title: "EduLift - Advanced Talent Identification Platform",
    description: "AI-powered educational platform for Sri Lankan students with personality-based career matching, multiple intelligence assessment, and ML-based authentication.",
    fullDescription: `EduLift is a comprehensive AI-powered educational platform specifically designed to assist Sri Lankan students in making informed decisions after completing their O-Level examinations. The platform combines modern web technologies with advanced machine learning algorithms to provide personalized career guidance, talent identification, and educational assessment tools.

The system addresses significant challenges when transitioning from O-Level to higher education and career paths, utilizing guidance, limited career awareness, and insufficient talent identification systems. EduLift provides AI-powered career guidance with personality-based career matching using Big Five model, talent identification through Multiple Intelligence Theory implementation for aptitude assessment, handwriting verification with ML-based authentication for academic integrity, comprehensive role and exam management, and personalized learning with tailored recommendations based on individual profiles.`,
    image: "/assets/Projects/EduLift - Advanced Talent Identification Platform.png",
    gallery: [
      "/assets/Projects/EduLift - Advanced Talent Identification Platform.png",
      "/assets/Projects/EduLift - Advanced Talent Identification Platform.png",
      "/assets/Projects/EduLift - Advanced Talent Identification Platform.png"
    ],
    techStack: ["Next.js 14.0.3", "TypeScript", "Material-UI (MUI)", "React Context API", "Axios", "Formik", "Yup", "Chart.js", "AOS"],
    type: "Web App",
    industry: "Education",
    status: "Live",
    year: "2024-2025",
    featured: true,
    githubUrl: "https://github.com/lahiru/eduLift",
    metrics: [
      { label: "AI Models", value: "5+" },
      { label: "Target Users", value: "Students" },
      { label: "Region", value: "Sri Lanka" },
      { label: "Framework", value: "Next.js 14" }
    ],
    features: [
      "Personality-based career matching (Big Five)",
      "Multiple Intelligence assessment",
      "Handwriting verification for integrity",
      "Career guidance system",
      "Talent identification tools",
      "Personalized learning recommendations",
      "Market integration with salary data",
      "Development pathways"
    ],
    highlights: [
      "Addresses critical Sri Lankan education challenges",
      "AI-powered career matching and talent identification",
      "Multi-role architecture (Admin, Teacher, Student, Assistant)",
      "Comprehensive skill evaluation and career development"
    ],
    architecture: `Modern Next.js 14.0.3 with TypeScript for type safety, Material-UI for professional interface, React Context API for state management, HTTP client using Axios with interceptors, form handling with Formik and Yup validation, data visualization using Chart.js, animations with AOS (Animate On Scroll).`
  },
  {
    id: 9,
    title: "Blind-Supporter Web Application",
    description: "Accessibility-focused web application providing essential support services for visually impaired users, enhancing their daily lives.",
    fullDescription: `Blind-Supporter is a dedicated web application designed to provide comprehensive support services for visually impaired users. The platform features an intuitive, accessible interface that facilitates easy navigation and interaction for users with visual impairments.

Built with .NET framework, the application provides essential services and resources that enhance accessibility and improve the quality of life for the visually impaired community.`,
    image: "/assets/Projects/Blind-Supporter Web Application.png",
    gallery: [
      "/assets/Projects/Blind-Supporter Web Application.png",
      "/assets/Projects/Blind-Supporter Web Application.png",
      "/assets/Projects/Blind-Supporter Web Application.png"
    ],
    techStack: ["C#", ".NET Framework"],
    type: "Web App",
    industry: "Accessibility",
    status: "Completed",
    year: "2023",
    featured: false,
    githubUrl: "https://github.com/lahiru/blind-supporter",
    metrics: [
      { label: "Focus", value: "Accessibility" },
      { label: "Platform", value: ".NET" },
      { label: "Users", value: "Visually Impaired" },
      { label: "Purpose", value: "Support Services" }
    ],
    features: [
      "Accessibility-focused design",
      "Intuitive interface for visually impaired",
      "Easy navigation and interaction",
      "Essential support services",
      "Screen reader compatibility",
      "High contrast interfaces"
    ],
    highlights: [
      "Designed specifically for visually impaired users",
      "Provides essential daily support services",
      "Enhances accessibility and independence",
      "Built with robust .NET Framework"
    ],
    architecture: `Developed using C# with .NET framework, leveraging .NET Core for building a robust Web API. Features seamless integration with other services and platforms through API integration, improving overall functionality.`
  },
  {
    id: 10,
    title: "Online Food Delivery Management System",
    description: "User management system facilitating registration, login, and profile management for customers and delivery personnel.",
    fullDescription: `A comprehensive online food delivery management system built with C# that streamlines the food ordering and delivery process. The platform features robust user management, order processing, and delivery tracking capabilities.

The system enables efficient order placement and tracking, manages customer and delivery personnel profiles, and includes an admin interface for managing menus, tracking orders, and overseeing delivery operations.`,
    image: "/assets/Projects/Online Food Delivery Management System.png",
    gallery: [
      "/assets/Projects/Online Food Delivery Management System.png",
      "/assets/Projects/Online Food Delivery Management System.png",
      "/assets/Projects/Online Food Delivery Management System.png"
    ],
    techStack: ["C#", ".NET", "SQL Server"],
    type: "Web App",
    industry: "Food Delivery",
    status: "Completed",
    year: "2023",
    featured: false,
    githubUrl: "https://github.com/lahiru/food-delivery",
    metrics: [
      { label: "Users", value: "Multi-role" },
      { label: "Features", value: "Full Management" },
      { label: "Platform", value: ".NET" },
      { label: "Database", value: "SQL Server" }
    ],
    features: [
      "User registration and authentication",
      "Profile management for customers and delivery personnel",
      "Order processing and tracking",
      "Menu management",
      "Delivery operations oversight",
      "Admin dashboard"
    ],
    highlights: [
      "Complete food delivery ecosystem",
      "Multi-role user management",
      "Efficient order processing workflow",
      "Robust backend with .NET"
    ],
    architecture: `Backend built with C# providing a robust framework for the application, .NET Core used for seamless service integration, SQL Server for database management.`
  },
  {
    id: 11,
    title: "Full-Stack Online Job Portal System",
    description: "Comprehensive job portal with specialized capabilities for Admin, Job Seeker, and Employer roles, real-time updates via Apache Kafka.",
    fullDescription: `A feature-rich job portal system built for YouTube educational content, demonstrating enterprise-level full-stack development practices. The platform utilizes React.js for a responsive UI, Spring Boot for scalable backend development, MySQL for efficient data storage and management, and Apache Kafka for real-time updates.

The system implements JWT and Spring Security for user authentication and authorization, ensuring secure data access. Docker is employed to containerize the application, facilitating smooth operation in various environments.`,
    image: "/assets/Projects/Full-Stack Online Job Portal System.png",
    gallery: [
      "/assets/Projects/Full-Stack Online Job Portal System.png",
      "/assets/Projects/Full-Stack Online Job Portal System.png",
      "/assets/Projects/Full-Stack Online Job Portal System.png"
    ],
    techStack: ["React.js", "Spring Boot", "MySQL", "Apache Kafka", "Docker", "JWT"],
    type: "Web App",
    industry: "Job Portal",
    status: "Live",
    year: "2022-Present",
    featured: false,
    liveUrl: "https://youtube.com/job-portal-tutorial",
    githubUrl: "https://github.com/lahiru/job-portal",
    metrics: [
      { label: "User Roles", value: "3 Types" },
      { label: "Real-time", value: "Kafka" },
      { label: "Security", value: "JWT" },
      { label: "Container", value: "Docker" }
    ],
    features: [
      "Multi-role user system (Admin, Job Seeker, Employer)",
      "Real-time job updates with Apache Kafka",
      "Secure authentication with JWT",
      "Containerized deployment with Docker",
      "Responsive React.js interface",
      "Scalable Spring Boot backend"
    ],
    highlights: [
      "Educational full-stack development tutorial",
      "Enterprise-level architecture and practices",
      "Real-time event streaming with Kafka",
      "Complete authentication and authorization flow"
    ],
    architecture: `Frontend built with React.js for responsive UI, Spring Boot backend for scalable microservices, MySQL for data persistence, Apache Kafka for real-time messaging, Docker for containerization, and JWT with Spring Security for authentication.`
  },
  {
    id: 12,
    title: "Point-Of-Sale (POS) Systems for Inventory Management",
    description: "Dynamic, data-driven web applications with real-time client-server communication, interactive charts, and predictive analytics.",
    fullDescription: `A comprehensive POS and inventory management system built for YouTube educational content, demonstrating enterprise-level web development practices. The platform combines PHP and MySQL to create dynamic, data-driven web applications with real-time client-server communication using AJAX.

The system utilizes database efficiency through triggers, functions, and stored procedures to automate tasks and enhance data management and control. User experience focuses on delivering feature-rich applications with advanced data analysis and interactive visualizations through Chart.js and D3.js.`,
    image: "/assets/Projects/Point-Of-Sale (POS) Systems for Inventory Management.png",
    gallery: [
      "/assets/Projects/Point-Of-Sale (POS) Systems for Inventory Management.png",
      "/assets/Projects/Point-Of-Sale (POS) Systems for Inventory Management.png",
      "/assets/Projects/Point-Of-Sale (POS) Systems for Inventory Management.png"
    ],
    techStack: ["PHP", "MySQL", "AJAX", "Chart.js", "D3.js", "Machine Learning"],
    type: "Web App",
    industry: "Retail",
    status: "Live",
    year: "2022-Present",
    featured: false,
    liveUrl: "https://youtube.com/pos-tutorial",
    githubUrl: "https://github.com/lahiru/pos-system",
    metrics: [
      { label: "Features", value: "Full POS" },
      { label: "Analytics", value: "Predictive" },
      { label: "Real-time", value: "AJAX" },
      { label: "Visualizations", value: "Interactive" }
    ],
    features: [
      "Real-time client-server communication",
      "Database efficiency with triggers and functions",
      "Interactive data visualization",
      "Predictive analytics with ML",
      "Dynamic inventory management",
      "Advanced reporting system"
    ],
    highlights: [
      "Educational content for web development",
      "Enterprise-level POS implementation",
      "Integrates machine learning for predictions",
      "Advanced data visualization with Chart.js and D3.js"
    ],
    architecture: `Backend developed with PHP and MySQL, real-time updates using AJAX, interactive charts with Chart.js and D3.js for data analysis, machine learning integration for predictive analytics and insights.`
  },
  {
    id: 13,
    title: "Full-Stack Food Ordering App",
    description: "Cross-platform mobile app with onboarding, login options, product management, shopping cart, checkout, and multimedia communication.",
    fullDescription: `A comprehensive food ordering mobile application developed using Flutter for cross-platform compatibility, ensuring a dynamic, responsive, and feature-rich application. The backend leverages Firebase for authentication, cloud services, and Visual Studio for IDE, ensuring efficient development.

The application provides consumer features including onboarding and login options (Facebook, email, SMS), product categories and favorites management, shopping cart with seamless checkout, and multiple payment options (Stripe, cash on delivery).`,
    image: "/assets/Projects/Full-Stack Food Ordering App.png",
    gallery: [
      "/assets/Projects/Full-Stack Food Ordering App.png",
      "/assets/Projects/Full-Stack Food Ordering App.png",
      "/assets/Projects/Full-Stack Food Ordering App.png"
    ],
    techStack: ["Flutter", "Firebase", "Visual Studio", "Stripe API"],
    type: "Mobile App",
    industry: "Food Delivery",
    status: "Live",
    year: "2022-Present",
    featured: false,
    liveUrl: "https://youtube.com/food-ordering-app",
    githubUrl: "https://github.com/lahiru/food-ordering-flutter",
    metrics: [
      { label: "Platform", value: "Cross-platform" },
      { label: "Auth", value: "Multi-option" },
      { label: "Payment", value: "Stripe" },
      { label: "Backend", value: "Firebase" }
    ],
    features: [
      "Onboarding and multi-option login (Facebook, email, SMS)",
      "Product categories and favorites management",
      "Shopping cart with seamless checkout",
      "Multiple payment options (Stripe, cash)",
      "Seller dashboard for product management",
      "Push notifications and communication"
    ],
    highlights: [
      "Educational Flutter development tutorial",
      "Complete e-commerce mobile solution",
      "Multi-stakeholder platform design",
      "Real-time order tracking and notifications"
    ],
    architecture: `Flutter for cross-platform mobile development, Firebase for backend services (authentication, Firestore, storage), Stripe API for payment processing, and Visual Studio as the development IDE.`
  },
  {
    id: 14,
    title: "Garbage Collection Web-Site",
    description: "Responsive web application with API for seamless interaction and data retrieval related to garbage collection services.",
    fullDescription: `A web-based garbage collection management system built with C# and SQL Server that provides a responsive design for optimal viewing across devices. The platform includes an API for seamless interaction and efficient data retrieval, making garbage collection services more accessible and manageable.

The system is designed to streamline garbage collection operations, provide tracking capabilities, and offer an intuitive interface for both service providers and customers.`,
    image: "/assets/Projects/Garbage Collection Web-Site.png",
    gallery: [
      "/assets/Projects/Garbage Collection Web-Site.png",
      "/assets/Projects/Garbage Collection Web-Site.png",
      "/assets/Projects/Garbage Collection Web-Site.png"
    ],
    techStack: ["C#", "SQL Server", ".NET", "Web API"],
    type: "Web App",
    industry: "Municipal Services",
    status: "Completed",
    year: "2023",
    featured: false,
    githubUrl: "https://github.com/lahiru/garbage-collection",
    metrics: [
      { label: "Design", value: "Responsive" },
      { label: "API", value: "RESTful" },
      { label: "Database", value: "SQL Server" },
      { label: "Platform", value: ".NET" }
    ],
    features: [
      "Responsive design for all devices",
      "RESTful API for data interaction",
      "Garbage collection tracking",
      "Service scheduling",
      "Customer management",
      "Admin dashboard"
    ],
    highlights: [
      "Streamlines municipal waste management",
      "Responsive across all device sizes",
      "Efficient API for service integration",
      "Robust SQL Server database"
    ],
    architecture: `Backend developed using C# for robust application logic, SQL Server for reliable database management, .NET framework for seamless API integration, responsive web design for optimal cross-device experience.`
  }
];

export default realProjects;
